'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Skeleton Component
 *
 * A loading placeholder component with pulse animation.
 * Uses design tokens for consistent styling across light/dark modes.
 *
 * @example
 * ```tsx
 * // Simple skeleton
 * <Skeleton className="h-12 w-12 rounded-full" />
 *
 * // Text skeleton
 * <Skeleton className="h-4 w-full" />
 *
 * // Card skeleton
 * <div className="space-y-3">
 *   <Skeleton className="h-32 w-full rounded-lg" />
 *   <Skeleton className="h-4 w-3/4" />
 *   <Skeleton className="h-4 w-1/2" />
 * </div>
 *
 * // Avatar skeleton
 * <Skeleton className="h-10 w-10 rounded-full" />
 *
 * // Button skeleton
 * <Skeleton className="h-10 w-32 rounded-md" />
 * ```
 */
const Skeleton = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'animate-pulse rounded-md bg-neutral-200 dark:bg-neutral-700',
      className
    )}
    {...props}
  />
));

Skeleton.displayName = 'Skeleton';

export { Skeleton };

/**
 * Skeleton Utilities for common patterns
 */

/**
 * Text Skeleton - Multiple lines of text with natural width variation
 * @example
 * <SkeletonText lines={3} />
 */
export function SkeletonText({
  lines = 1,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => {
        // Last line is typically shorter
        const width = i === lines - 1 ? 'w-1/3' : 'w-full';
        return <Skeleton key={i} className={cn('h-4', width)} />;
      })}
    </div>
  );
}

/**
 * Card Skeleton - A complete card loading skeleton
 * @example
 * <SkeletonCard />
 */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-4 rounded-lg border border-neutral-200 p-4 dark:border-neutral-800', className)}>
      <Skeleton className="h-32 w-full rounded-md" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

/**
 * Avatar Skeleton - Circular avatar loading skeleton
 * @example
 * <SkeletonAvatar />
 */
export function SkeletonAvatar({
  size = 'md',
}: {
  size?: 'sm' | 'md' | 'lg';
} = {}) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  return (
    <Skeleton
      className={cn('rounded-full', sizeClasses[size])}
    />
  );
}

/**
 * List Skeleton - Loading skeleton for list items
 * @example
 * <SkeletonList count={5} />
 */
export function SkeletonList({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
} = {}) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <SkeletonAvatar />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Button Skeleton - Loading skeleton for button
 * @example
 * <SkeletonButton />
 */
export function SkeletonButton({
  size = 'md',
  className,
}: {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
} = {}) {
  const sizeClasses = {
    sm: 'h-9 w-24',
    md: 'h-10 w-32',
    lg: 'h-11 w-40',
  };

  return (
    <Skeleton className={cn('rounded-md', sizeClasses[size], className)} />
  );
}
